import { Component, ComponentFactoryResolver, OnInit, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { TokenService } from '@shared/service/app/token.service';
import { filter } from 'rxjs';
import { CustomSnackbarComponent } from '@shared/components/custom-snackbar/custom.component';

import { AppTourComponent } from '@shared/components/tour/tour.component';

@Component({
    selector   : 'app-root',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss'],
    standalone : true,
    imports    : [RouterOutlet, AppTourComponent],
})
export class AppComponent implements OnInit {
    nombre_empresa: string;
    favicon: string;
    private originalTitle: string = 'JEDSTION';
    private defaultFavicon: string = '/assets/images/logo/JEDSTION_rayo_lima.svg';
    private defaultPrimaryColor: string = '#2196f3';
    private forceReload = false;

    /**
     * Constructor
     */
    constructor(
        private titulo: Title,
        private _empresaService: EmpresaService,
        private resolver: ComponentFactoryResolver,
        private viewContainerRef: ViewContainerRef,
        private router: Router,
        // private _tokenService: TokenService
    ) {
    }

    ngOnInit(): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                registrations.forEach(reg => {
                    const scriptUrl = reg.active?.scriptURL || '';
                    if (scriptUrl.includes('ngsw-worker.js')) {
                        reg.unregister().then(() => {
                            console.log('Service Worker de Angular eliminado:', scriptUrl);
                        });
                        location.reload();
                    } else {
                        console.log('SW no eliminado (se mantiene):', scriptUrl);
                    }
                });
            });
        }
        // Obtén los datos de la empresa al inicializar el componente
        this.obtenerDatosEmpresa();
    }

    ngAfterViewInit(){
        if (localStorage.getItem('app-updated')) {
            if(window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone){
                this.showCustomSnackbar('La APP ha sido actualizada');
            }
            setTimeout(() => {
                this.viewContainerRef.clear();
            }, 5000);
            localStorage.removeItem('app-updated');
        }
    }

    obtenerDatosEmpresa(): void {
        // Utiliza el servicio EmpresaService para obtener los datos de la empresa
        this._empresaService.empresa$.subscribe(empresaData => {
            if (empresaData) {
                // Asigna el nombre de la empresa para mostrarlo en el título
                this.nombre_empresa = empresaData.razon_social;

                // Establece el título del documento HTML con el nombre de la empresa
                let tituloEmpresa = '';
                if (this.nombre_empresa){
                    tituloEmpresa = this.nombre_empresa;
                } else {
                    tituloEmpresa = this.originalTitle;
                }
                // SE COMENTA para que CustomTitleStrategy tenga el control del Title
                // this.titulo.setTitle(tituloEmpresa);

                // Guarda el favicon de la empresa
                this.favicon = empresaData.favicon || this.defaultFavicon;

                // Establece el favicon en el documento HTML
                this.setFavicon(this.favicon);

                // Aplica el color primario de la empresa al theme global
                this.applyPrimaryTheme(empresaData.color_primario);
            }
        });
    }

    private applyPrimaryTheme(primaryColor?: string): void {
        const rgb = this.parseToRgb(primaryColor) || this.parseToRgb(this.defaultPrimaryColor);
        if (!rgb) {
            return;
        }

        const primarySteps: Record<number, number> = {
            50: 0.9,
            100: 0.75,
            200: 0.6,
            300: 0.45,
            400: 0.2,
            500: 0,
            600: 0.12,
            700: 0.24,
            800: 0.36,
            900: 0.5
        };

        Object.entries(primarySteps).forEach(([step, ratio]) => {
            const numericStep = Number(step);
            const mixed = numericStep <= 500
                ? this.mixRgb(rgb, [255, 255, 255], ratio)
                : this.mixRgb(rgb, [0, 0, 0], ratio);

            this.setFuseColorVar(`--fuse-primary-${numericStep}`, mixed);
            this.setFuseColorVar(`--fuse-on-primary-${numericStep}`, this.getContrastRgb(mixed));
        });

        this.setFuseColorVar('--fuse-primary', rgb);
        this.setFuseColorVar('--fuse-on-primary', this.getContrastRgb(rgb));
    }

    private setFuseColorVar(name: string, rgb: [number, number, number]): void {
        const value = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        document.documentElement.style.setProperty(name, value);
        document.body.style.setProperty(name, value);
        document.documentElement.style.setProperty(`${name}-rgb`, `${rgb[0]},${rgb[1]},${rgb[2]}`);
        document.body.style.setProperty(`${name}-rgb`, `${rgb[0]},${rgb[1]},${rgb[2]}`);
    }

    private parseToRgb(color?: string): [number, number, number] | null {
        if (!color || !color.trim()) {
            return null;
        }

        const helper = document.createElement('div');
        helper.style.color = '';
        helper.style.color = color.trim();

        if (!helper.style.color) {
            return null;
        }

        document.body.appendChild(helper);
        const computedColor = getComputedStyle(helper).color;
        helper.remove();

        const match = computedColor.match(/\d+/g);
        if (!match || match.length < 3) {
            return null;
        }

        return [Number(match[0]), Number(match[1]), Number(match[2])];
    }

    private mixRgb(from: [number, number, number], to: [number, number, number], ratio: number): [number, number, number] {
        const safeRatio = Math.max(0, Math.min(1, ratio));
        return [
            Math.round(from[0] + (to[0] - from[0]) * safeRatio),
            Math.round(from[1] + (to[1] - from[1]) * safeRatio),
            Math.round(from[2] + (to[2] - from[2]) * safeRatio)
        ];
    }

    private getContrastRgb(rgb: [number, number, number]): [number, number, number] {
        const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
        return luminance > 0.62 ? [0, 0, 0] : [255, 255, 255];
    }

    // Método para establecer el favicon en el documento HTML
    setFavicon(url: string): void {
        const link: HTMLLinkElement = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        link.href = url;
        document.getElementsByTagName('head')[0].appendChild(link);
    }

    // Método para restablecer el favicon y el título original al cerrar sesión
    resetFaviconAndTitle(): void {
        // Restablecer el título original del documento HTML
        // SE COMENTA para que CustomTitleStrategy tenga el control
        // this.titulo.setTitle(this.originalTitle);

        // Restablecer el favicon por defecto
        this.setFavicon(this.defaultFavicon);
    }

    showCustomSnackbar(message: string): void {
        this.viewContainerRef.clear();
        const factory = this.resolver.resolveComponentFactory(CustomSnackbarComponent);
        const componentRef = this.viewContainerRef.createComponent(factory);
        componentRef.instance.message = message;
    }
}
